<template>
  <v-container fluid class="container">
    <v-card class="mb-4">
      <v-card-title>
        New Ticket
        <v-chip v-if="entityLabel" class="ml-2" x-small outlined>{{ entityLabel }}</v-chip>
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="form.subject"
          outlined
          counter="200"
          maxlength="200"
          label="Subject *"
        />
        <v-textarea
          v-model="form.message"
          outlined
          label="Message *"
          auto-grow
          rows="3"
        />
        <v-row>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.planning_id"
              :items="planningOptions"
              :loading="planningsLoading"
              item-text="label"
              item-value="id"
              outlined
              clearable
              label="Planning *"
              no-data-text="No plannings available"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.priority"
              :items="priorityOptions"
              outlined
              label="Priority *"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.category"
              :items="categoryOptions"
              outlined
              label="Category *"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.app_area"
              :items="appAreaOptions"
              outlined
              label="Application area *"
            />
          </v-col>
        </v-row>

        <v-file-input
          v-model="form.attachments"
          multiple
          chips
          small-chips
          show-size
          label="Attachments"
          prepend-icon="mdi-paperclip"
        />
        <div class="mt-2">
          <v-btn
            small
            outlined
            color="primary"
            :loading="pastingFromClipboard"
            @click="pasteImageFromClipboard"
          >
            Paste Image (PrintScreen)
          </v-btn>
        </div>
        <div class="text-caption grey--text text--darken-1">
          You can paste screenshot images with Ctrl+V or the button above.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="isAdmin"
          text
          color="secondary"
          :loading="testEmailSending"
          @click="sendTestEmail"
        >
          Send Test Email
        </v-btn>
        <v-spacer />
        <v-btn color="primary" :loading="submitting" @click="submitTicket">
          Add Ticket
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card>
      <v-card-title>
        Tickets
        <v-spacer />
        <v-switch
          v-model="hideClosedTickets"
          class="mr-4 mt-0"
          hide-details
          inset
          dense
          label="Hide closed"
        />
        <v-btn text small :loading="loading" @click="loadTickets">Refresh</v-btn>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="visibleTickets"
        :loading="loading"
        :items-per-page="25"
        dense
      >
        <template v-slot:item.actions="{ item }">
          <v-btn x-small text color="primary" @click="openTicketDetails(item.id)">
            View
          </v-btn>
        </template>

        <template v-slot:item.planning_id="{ item }">
          <router-link
            :to="`/plannings/${item.planning_id}`"
            target="_blank"
          >
            {{ item.planning_id }}
          </router-link>
        </template>

        <template v-slot:item.attachments="{ item }">
          <span>{{ item.attachments ? item.attachments.length : 0 }}</span>
        </template>

        <template v-slot:item.status="{ item }">
          <v-select
            dense
            hide-details
            :items="statusOptions"
            :value="item.status"
            :disabled="statusUpdatingId === item.id"
            @change="value => openStatusChangeDialog(item, value)"
          />
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="detailsDialog" max-width="900px">
      <v-card>
        <v-card-title>
          Ticket #{{ selectedTicket ? selectedTicket.id : '' }}
          <v-spacer />
          <v-btn icon @click="closeDetailsDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="detailsLoading">
          <v-progress-linear indeterminate />
        </v-card-text>
        <v-card-text v-else-if="selectedTicket">
          <div class="mb-2"><strong>Subject:</strong> {{ selectedTicket.subject }}</div>
          <div class="mb-2">
            <strong>Planning:</strong>
            <router-link
              :to="`/plannings/${selectedTicket.planning_id}`"
              target="_blank"
            >
              {{ selectedTicket.planning_id }}
            </router-link>
          </div>
          <div class="mb-2"><strong>Category:</strong> {{ selectedTicket.category || '-' }}</div>
          <div class="mb-2"><strong>App Area:</strong> {{ selectedTicket.app_area || '-' }}</div>
          <div class="mb-2"><strong>Priority:</strong> {{ selectedTicket.priority }}</div>
          <div class="mb-2"><strong>Status:</strong> {{ selectedTicket.status }}</div>
          <div class="mb-2"><strong>Message:</strong></div>
          <v-alert dense outlined type="info">{{ selectedTicket.message }}</v-alert>

          <div class="mt-4 mb-2"><strong>Status History</strong></div>
          <v-simple-table dense v-if="selectedTicket.events && selectedTicket.events.length">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>From</th>
                <th>To</th>
                <th>Reply</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="event in selectedTicket.events" :key="event.id">
                <td>{{ formatDate(event.changedAt) }}</td>
                <td>{{ event.changedByName || '-' }}</td>
                <td>{{ event.oldStatus || '-' }}</td>
                <td>{{ event.newStatus }}</td>
                <td>{{ event.replyMessage || '-' }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div v-else class="text-caption grey--text">No status changes yet.</div>

          <div class="mt-4 mb-2"><strong>Attachments</strong></div>
          <v-simple-table dense v-if="selectedTicket.attachments && selectedTicket.attachments.length">
            <thead>
              <tr>
                <th>Preview</th>
                <th>File</th>
                <th>Size</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="attachment in selectedTicket.attachments" :key="attachment.id">
                <td>
                  <div v-if="isImageAttachment(attachment)">
                    <v-progress-circular
                      v-if="attachmentPreviewLoading[attachment.id]"
                      indeterminate
                      size="18"
                      width="2"
                    />
                    <v-img
                      v-else-if="attachmentPreviewUrls[attachment.id]"
                      :src="attachmentPreviewUrls[attachment.id]"
                      max-width="72"
                      max-height="72"
                      contain
                    />
                    <span v-else class="text-caption grey--text">No preview</span>
                  </div>
                  <span v-else class="text-caption grey--text">-</span>
                </td>
                <td>{{ attachment.fileName }}</td>
                <td>{{ formatFileSize(attachment.fileSize) }}</td>
                <td>
                  <v-btn
                    x-small
                    text
                    color="primary"
                    :loading="downloadingAttachmentId === attachment.id"
                    @click="downloadAttachment(attachment)"
                  >
                    Download
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <div v-else class="text-caption grey--text">No attachments.</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="statusDialog" max-width="640px">
      <v-card>
        <v-card-title>
          Change Status
          <v-spacer />
          <v-btn icon @click="closeStatusChangeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="mb-2">
            Ticket #{{ pendingStatusTicketId || '-' }}
          </div>
          <div class="mb-4">
            From <strong>{{ pendingStatusCurrent || '-' }}</strong> to
            <strong>{{ pendingStatusValue || '-' }}</strong>
          </div>
          <v-textarea
            v-model="pendingStatusReply"
            outlined
            auto-grow
            rows="3"
            label="Reply message (optional)"
          />
          <div class="text-caption grey--text">
            This message is stored in status history and included in status-change emails.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeStatusChangeDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="statusUpdatingId === pendingStatusTicketId"
            @click="confirmStatusChange"
          >
            Update Status
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Snackbar :text="notificationText" :type="notificationType" @close="clearNotification" />
  </v-container>
</template>

<script>
import Snackbar from '../components/Snackbar';
import api from '../api/lbt-api';

const {
  getTickets,
  getTicket,
  createTicket,
  getTicketAttachmentBlob,
  downloadTicketAttachment,
  sendTicketTestEmail,
  updateTicketStatus,
  getTicketMetadata,
  getTicketPlanningOptions,
} = api;

export default {
  name: 'Tickets',
  components: {
    Snackbar,
  },
  data() {
    return {
      loading: false,
      submitting: false,
      testEmailSending: false,
      statusUpdatingId: null,
      statusDialog: false,
      pendingStatusTicketId: null,
      pendingStatusCurrent: '',
      pendingStatusValue: '',
      pendingStatusReply: '',
      notificationText: '',
      notificationType: '',
      notificationTimer: null,
      tickets: [],
      hideClosedTickets: true,
      detailsDialog: false,
      detailsLoading: false,
      selectedTicket: null,
      downloadingAttachmentId: null,
      attachmentPreviewUrls: {},
      attachmentPreviewLoading: {},
      headers: [
        { text: 'ID', value: 'id', sortable: true },
        { text: 'Subject', value: 'subject', sortable: true },
        { text: 'Category', value: 'category', sortable: true },
        { text: 'App Area', value: 'app_area', sortable: true },
        { text: 'Planning', value: 'planning_id', sortable: true },
        { text: 'Priority', value: 'priority', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Attachments', value: 'attachments', sortable: false },
        { text: 'Created By', value: 'created_by_name', sortable: true },
        { text: 'Created At', value: 'created_at', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      form: {
        subject: '',
        message: '',
        category: '',
        app_area: '',
        planning_id: null,
        priority: 'medium',
        attachments: [],
      },
      pastingFromClipboard: false,
      planningsLoading: false,
      planningOptions: [],
      priorityOptions: ['low', 'medium', 'high', 'critical'],
      statusOptions: ['new', 'in_progress', 'waiting_user', 'resolved', 'closed'],
      categoryOptions: [],
      appAreaOptions: [],
      entityLabel: '',
      isAdmin: false,
    };
  },
  computed: {
    visibleTickets() {
      if (!this.hideClosedTickets) {
        return this.tickets;
      }

      return this.tickets.filter(ticket => String(ticket.status || '').toLowerCase() !== 'closed');
    },
  },
  async created() {
    window.addEventListener('paste', this.handleClipboardPaste);
    await this.loadTicketMetadata();
    await this.loadPlanningOptions();
    await this.loadTickets();
  },
  beforeDestroy() {
    window.removeEventListener('paste', this.handleClipboardPaste);
    if (this.notificationTimer) {
      clearTimeout(this.notificationTimer);
      this.notificationTimer = null;
    }
    this.clearAttachmentPreviews();
  },
  watch: {
    detailsDialog(value) {
      if (!value) {
        this.closeDetailsDialog();
      }
    },
    statusDialog(value) {
      if (!value) {
        this.closeStatusChangeDialog();
      }
    },
  },
  methods: {
    async sendTestEmail() {
      this.testEmailSending = true;
      try {
        const response = await sendTicketTestEmail({});
        const recipient = response?.mail?.recipient || 'info@zame.dev';
        this.notify(`Test email sent to ${recipient}.`, 'success');
      } catch (error) {
        this.notify(error.message || 'Failed to send test email.', 'error');
      } finally {
        this.testEmailSending = false;
      }
    },
    async loadTicketMetadata() {
      try {
        const metadata = await getTicketMetadata();
        if (metadata.priorities && metadata.priorities.length) {
          this.priorityOptions = metadata.priorities;
        }
        if (metadata.statuses && metadata.statuses.length) {
          this.statusOptions = metadata.statuses;
        }
        this.categoryOptions = metadata.categories || [];
        this.appAreaOptions = metadata.app_areas || [];
        this.entityLabel = metadata.entity || '';
        this.isAdmin = metadata.is_admin === true;
      } catch (error) {
        this.notify(error.message || 'Failed to load ticket metadata.', 'warning');
      }
    },
    async loadPlanningOptions() {
      this.planningsLoading = true;
      try {
        const response = await getTicketPlanningOptions();
        this.planningOptions = response.plannings || [];
      } catch (error) {
        this.notify(error.message || 'Failed to load plannings.', 'error');
      } finally {
        this.planningsLoading = false;
      }
    },
    async loadTickets() {
      this.loading = true;
      try {
        const response = await getTickets({ page: 1, size: 200 });
        this.tickets = response.items || [];
      } catch (error) {
        this.notify(error.message || 'Failed to load tickets.', 'error');
      } finally {
        this.loading = false;
      }
    },
    async submitTicket() {
      const subject = String(this.form.subject || '').trim();
      const message = String(this.form.message || '').trim();
      const category = String(this.form.category || '').trim();
      const appArea = String(this.form.app_area || '').trim();
      const planningId = Number(this.form.planning_id);

      if (!subject) {
        this.notify('Subject is required.', 'error');
        return;
      }
      if (!message) {
        this.notify('Message is required.', 'error');
        return;
      }
      if (!Number.isInteger(planningId) || planningId <= 0) {
        this.notify('Planning must be selected.', 'error');
        return;
      }
      if (!category) {
        this.notify('Category is required.', 'error');
        return;
      }
      if (!appArea) {
        this.notify('Application area is required.', 'error');
        return;
      }

      const payload = new FormData();
      payload.append('subject', subject);
      payload.append('message', message);
      payload.append('category', category);
      payload.append('app_area', appArea);
      payload.append('planning_id', String(planningId));
      payload.append('priority', this.form.priority);

      (this.form.attachments || []).forEach((file) => {
        payload.append('attachments', file);
      });

      this.submitting = true;
      try {
        const response = await createTicket(payload);
        this.form = {
          subject: '',
          message: '',
          category: '',
          app_area: '',
          planning_id: null,
          priority: 'medium',
          attachments: [],
        };
        if (response.mail && response.mail.sent === false) {
          this.notify(`Ticket added, but email failed: ${response.mail.error || 'unknown error'}`, 'warning');
        } else {
          this.notify('Ticket added successfully.', 'success');
        }
        await this.loadTickets();
      } catch (error) {
        this.notify(error.message || 'Failed to create ticket.', 'error');
      } finally {
        this.submitting = false;
      }
    },
    openStatusChangeDialog(ticket, value) {
      const newStatus = String(value || '').trim();
      const ticketId = ticket.id;
      const idx = this.tickets.findIndex(item => item.id === ticketId);
      if (idx === -1) {
        return;
      }

      const currentTicket = this.tickets[idx];
      if (!newStatus || currentTicket.status === newStatus) {
        return;
      }

      this.pendingStatusTicketId = ticketId;
      this.pendingStatusCurrent = currentTicket.status;
      this.pendingStatusValue = newStatus;
      this.pendingStatusReply = '';
      this.statusDialog = true;
    },
    closeStatusChangeDialog() {
      this.statusDialog = false;
      this.pendingStatusTicketId = null;
      this.pendingStatusCurrent = '';
      this.pendingStatusValue = '';
      this.pendingStatusReply = '';
    },
    async confirmStatusChange() {
      const ticketId = this.pendingStatusTicketId;
      const newStatus = String(this.pendingStatusValue || '').trim();
      const replyMessage = String(this.pendingStatusReply || '').trim();
      const idx = this.tickets.findIndex(item => item.id === ticketId);
      if (!ticketId || !newStatus || idx === -1) {
        return;
      }

      this.statusUpdatingId = ticketId;

      try {
        const response = await updateTicketStatus(ticketId, newStatus, replyMessage);
        if (response.ticket) {
          this.$set(this.tickets, idx, response.ticket);
          if (this.selectedTicket && this.selectedTicket.id === ticketId) {
            this.selectedTicket = this.normalizeTicket(response.ticket);
          }
        }
        if (response.mail && response.mail.sent === false) {
          this.notify(`Status updated, but email failed: ${response.mail.error || 'unknown error'}`, 'warning');
        } else {
          this.notify('Ticket status updated.', 'success');
        }
        this.closeStatusChangeDialog();
      } catch (error) {
        this.notify(error.message || 'Failed to update ticket status.', 'error');
      } finally {
        this.statusUpdatingId = null;
      }
    },
    async openTicketDetails(ticketId) {
      this.detailsDialog = true;
      this.detailsLoading = true;
      this.selectedTicket = null;
      this.clearAttachmentPreviews();
      try {
        const response = await getTicket(ticketId);
        this.selectedTicket = this.normalizeTicket(response.ticket || null);
        await this.loadAttachmentPreviews();
      } catch (error) {
        this.notify(error.message || 'Failed to load ticket details.', 'error');
      } finally {
        this.detailsLoading = false;
      }
    },
    normalizeTicket(ticket) {
      if (!ticket) {
        return null;
      }

      const attachments = Array.isArray(ticket.attachments)
        ? ticket.attachments.map(attachment => this.normalizeAttachment(attachment))
        : [];
      const events = Array.isArray(ticket.events)
        ? ticket.events.map(event => this.normalizeEvent(event))
        : [];

      return {
        ...ticket,
        attachments,
        events,
      };
    },
    normalizeAttachment(attachment) {
      const fileNameKey = 'file_name';
      const mimeTypeKey = 'mime_type';
      const fileSizeKey = 'file_size';
      return {
        ...attachment,
        fileName: attachment[fileNameKey] || attachment.fileName || '',
        mimeType: attachment[mimeTypeKey] || attachment.mimeType || '',
        fileSize: attachment[fileSizeKey] || attachment.fileSize || 0,
      };
    },
    normalizeEvent(event) {
      const oldStatusKey = 'old_status';
      const newStatusKey = 'new_status';
      const changedByUserIdKey = 'changed_by_user_id';
      const changedByNameKey = 'changed_by_name';
      const changedAtKey = 'changed_at';
      const replyMessageKey = 'reply_message';

      return {
        ...event,
        oldStatus: event[oldStatusKey] || event.oldStatus || '',
        newStatus: event[newStatusKey] || event.newStatus || '',
        changedByUserId: event[changedByUserIdKey] || event.changedByUserId || null,
        changedByName: event[changedByNameKey] || event.changedByName || '',
        changedAt: event[changedAtKey] || event.changedAt || null,
        replyMessage: event[replyMessageKey] || event.replyMessage || '',
      };
    },
    closeDetailsDialog() {
      this.detailsDialog = false;
      this.selectedTicket = null;
      this.detailsLoading = false;
      this.downloadingAttachmentId = null;
      this.clearAttachmentPreviews();
    },
    clearAttachmentPreviews() {
      Object.keys(this.attachmentPreviewUrls).forEach((attachmentId) => {
        const previewUrl = this.attachmentPreviewUrls[attachmentId];
        if (previewUrl) {
          window.URL.revokeObjectURL(previewUrl);
        }
      });
      this.attachmentPreviewUrls = {};
      this.attachmentPreviewLoading = {};
    },
    isImageAttachment(attachment) {
      const mimeType = String(attachment?.mimeType || '').toLowerCase();
      if (mimeType.startsWith('image/')) {
        return true;
      }

      const fileName = String(attachment?.fileName || '').toLowerCase();
      return ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'].some(ext => fileName.endsWith(ext));
    },
    async loadAttachmentPreviews() {
      if (!this.selectedTicket || !Array.isArray(this.selectedTicket.attachments)) {
        return;
      }

      const imageAttachments = this.selectedTicket.attachments
        .filter(attachment => this.isImageAttachment(attachment));
      await Promise.all(imageAttachments.map(attachment => this.loadAttachmentPreview(attachment)));
    },
    async loadAttachmentPreview(attachment) {
      if (!attachment || !this.isImageAttachment(attachment)) {
        return;
      }
      if (this.attachmentPreviewUrls[attachment.id]) {
        return;
      }
      if (this.attachmentPreviewLoading[attachment.id]) {
        return;
      }

      this.$set(this.attachmentPreviewLoading, attachment.id, true);
      try {
        const blob = await getTicketAttachmentBlob(attachment.id);
        const previewUrl = window.URL.createObjectURL(blob);
        this.$set(this.attachmentPreviewUrls, attachment.id, previewUrl);
      } catch (error) {
        this.notify(`Preview failed for ${attachment.fileName}.`, 'warning');
      } finally {
        this.$set(this.attachmentPreviewLoading, attachment.id, false);
      }
    },
    attachmentSignature(file) {
      return `${file.type || 'unknown'}:${file.size || 0}`;
    },
    appendUniqueAttachments(files) {
      const existingAttachments = Array.isArray(this.form.attachments) ? this.form.attachments : [];
      const signatures = new Set();
      const uniqueFiles = [];

      files.forEach((file) => {
        const signature = this.attachmentSignature(file);
        if (!signatures.has(signature)) {
          signatures.add(signature);
          uniqueFiles.push(file);
        }
      });

      if (!uniqueFiles.length) {
        return 0;
      }

      this.form.attachments = [...existingAttachments, ...uniqueFiles];
      return uniqueFiles.length;
    },
    buildClipboardImageFile(blob, mimeType, fileNamePrefix) {
      const extension = (mimeType || 'image/png').split('/')[1] || 'png';
      const fileName = `${fileNamePrefix}.${extension}`;
      return new File([blob], fileName, { type: mimeType || 'image/png' });
    },
    handleClipboardPaste(event) {
      const clipboardItems = event?.clipboardData?.items || [];
      const clipboardFiles = event?.clipboardData?.files || [];
      const imageFilesFromItems = [];
      const imageFilesFromFiles = [];
      const timestamp = Date.now();

      Array.from(clipboardItems).forEach((item, idx) => {
        const isImageType = item.type && item.type.startsWith('image/');
        if (isImageType) {
          const pastedImage = item.getAsFile();
          if (pastedImage) {
            imageFilesFromItems.push(
              this.buildClipboardImageFile(
                pastedImage,
                pastedImage.type,
                `clipboard-${timestamp}-item-${idx}`,
              ),
            );
          }
        }
      });

      Array.from(clipboardFiles).forEach((file, idx) => {
        const isImageType = file.type && file.type.startsWith('image/');
        if (isImageType) {
          imageFilesFromFiles.push(
            this.buildClipboardImageFile(
              file,
              file.type,
              `clipboard-${timestamp}-file-${idx}`,
            ),
          );
        }
      });

      const imageFiles = imageFilesFromItems.length
        ? imageFilesFromItems
        : imageFilesFromFiles;

      if (imageFiles.length) {
        event.preventDefault();
        const addedCount = this.appendUniqueAttachments(imageFiles);
        if (addedCount > 0) {
          this.notify(`${addedCount} screenshot attachment(s) added.`, 'success');
        } else {
          this.notify('Screenshot already attached. Duplicate was skipped.', 'warning');
        }
      }
    },
    async pasteImageFromClipboard() {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        this.notify('Clipboard read is not supported here. Use Ctrl+V instead.', 'warning');
        return;
      }

      this.pastingFromClipboard = true;
      try {
        const clipboardItems = await navigator.clipboard.read();
        const timestamp = Date.now();
        const pendingImages = [];

        clipboardItems.forEach((item, itemIndex) => {
          const imageType = item.types.find(type => type.startsWith('image/'));
          if (imageType) {
            pendingImages.push(
              item.getType(imageType).then(blob => this.buildClipboardImageFile(
                blob,
                imageType,
                `clipboard-${timestamp}-api-${itemIndex}`,
              )),
            );
          }
        });

        const clipboardImages = await Promise.all(pendingImages);
        const validImages = clipboardImages.filter(Boolean);

        if (!validImages.length) {
          this.notify('No image found in clipboard. Capture PrintScreen first.', 'warning');
          return;
        }

        const addedCount = this.appendUniqueAttachments(validImages);
        if (addedCount > 0) {
          this.notify(`${addedCount} clipboard image(s) added.`, 'success');
        } else {
          this.notify('Clipboard image already attached. Duplicate was skipped.', 'warning');
        }
      } catch (error) {
        this.notify('Could not read clipboard image. Use Ctrl+V after PrintScreen.', 'error');
      } finally {
        this.pastingFromClipboard = false;
      }
    },
    async downloadAttachment(attachment) {
      this.downloadingAttachmentId = attachment.id;
      try {
        const fileResponse = await downloadTicketAttachment(attachment.id, attachment.fileName);
        const objectUrl = window.URL.createObjectURL(fileResponse.blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileResponse.fileName || attachment.fileName || 'attachment';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      } catch (error) {
        this.notify(error.message || 'Failed to download attachment.', 'error');
      } finally {
        this.downloadingAttachmentId = null;
      }
    },
    formatFileSize(bytes) {
      const parsedBytes = Number(bytes || 0);
      if (!parsedBytes) {
        return '0 B';
      }
      if (parsedBytes < 1024) {
        return `${parsedBytes} B`;
      }
      if (parsedBytes < 1024 * 1024) {
        return `${(parsedBytes / 1024).toFixed(1)} KB`;
      }
      return `${(parsedBytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatDate(value) {
      if (!value) {
        return '';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleString();
    },
    clearNotification() {
      if (this.notificationTimer) {
        clearTimeout(this.notificationTimer);
        this.notificationTimer = null;
      }
      this.notificationText = '';
    },
    notify(text, type) {
      if (this.notificationTimer) {
        clearTimeout(this.notificationTimer);
        this.notificationTimer = null;
      }
      this.notificationText = text;
      this.notificationType = type;
      this.notificationTimer = setTimeout(() => {
        this.clearNotification();
      }, 10000);
    },
  },
};
</script>
